import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgxEchartsModule } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import { DatabaseService } from '../../../services/database.service';
import { LogService } from '../../../services/Logging.service';

// import { EnvironmentService } from '../../../services/enviroment.service';
// import { environment } from '../../../services/global-environment';;

import { environment } from '../../../services/global-environment';

interface ChemicalData {
  id: string;
  chineseName: string;
  englishName: string;
  casNumber: string;
  dangerLevel: number;
  departmentId: string;
  departmentName?: string;
  hazardClassifications: string[];
  lastUpdated: string;
  nextReviewDate: string;
  riskScore?: number;
  riskLevel?: string;
}

interface DepartmentData {
  id: string;
  name: string;
  location: string;
  chemicalCount: number;
  highRiskCount: number;
  riskScore: number;
}

interface TodoItem {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'completed';
  type: string;
}

@Component({
  selector: 'app-chemical-report',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxEchartsModule],
  templateUrl: './chemical-report.component.html',
  styleUrls: ['./chemical-report.component.scss']
})
export class ChemicalReportComponent implements OnInit {
  // Data
  chemicals: ChemicalData[] = [];
  departments: DepartmentData[] = [];
  todoItems: TodoItem[] = [];

  // Filter options
  selectedTimeframe: 'month' | 'quarter' | 'year' = 'quarter';
  selectedDepartment: string = 'all';

  // Loading state
  loading: boolean = true;
  error: string | null = null;

  // Charts options
  highRiskChartOption: any;
  departmentRiskChartOption: any;
  chemicalCountChartOption: any;
  expiryStatusChartOption: any;

  // Tabs
  activeTab: 'overview' | 'chemicals' | 'departments' | 'todos' = 'overview';

  constructor(
    private databaseService: DatabaseService,
    private logService: LogService
  ) {}

  ngOnInit(): void {
    this.loadAllData();
  }

  loadAllData(): void {
    this.loading = true;

    // In a real application, load from database
    // For now using mock data
    setTimeout(() => {
      this.loadChemicalData();
      this.loadDepartmentData();
      this.loadTodoItems();
      this.initializeCharts();

      this.loading = false;

      // Log the activity
      const companyId = environment.companyid;
      // this.logService.Loginlog(
      //   companyId,
      //   'ChemicalReport',
      //   'System',
      //   'Chemical report data loaded',
      //   'INFO'
      // );
    }, 1000);
  }

  loadChemicalData(): void {
    // Mock data for chemicals
    this.chemicals = [
      {
        id: 'chem-001',
        chineseName: '丙酮',
        englishName: 'Acetone',
        casNumber: '67-64-1',
        dangerLevel: 2,
        departmentId: 'dept-001',
        departmentName: '實驗研發部',
        hazardClassifications: ['易燃液體 類別2', '眼睛刺激 類別2'],
        lastUpdated: '2024-12-10',
        nextReviewDate: '2026-12-10',
        riskScore: 35.6,
        riskLevel: 'II'
      },
      {
        id: 'chem-002',
        chineseName: '甲醛',
        englishName: 'Formaldehyde',
        casNumber: '50-00-0',
        dangerLevel: 3,
        departmentId: 'dept-002',
        departmentName: '生產製造部',
        hazardClassifications: ['急毒性物質 類別3', '皮膚腐蝕/刺激 類別1B', '致癌物質 類別1B'],
        lastUpdated: '2024-09-15',
        nextReviewDate: '2025-03-15',
        riskScore: 78.5,
        riskLevel: 'IV'
      },
      {
        id: 'chem-003',
        chineseName: '苯',
        englishName: 'Benzene',
        casNumber: '71-43-2',
        dangerLevel: 3,
        departmentId: 'dept-001',
        departmentName: '實驗研發部',
        hazardClassifications: ['易燃液體 類別2', '致癌物質 類別1A', '生殖細胞致突變性物質 類別1B'],
        lastUpdated: '2024-07-22',
        nextReviewDate: '2025-01-22',
        riskScore: 82.7,
        riskLevel: 'V'
      },
      {
        id: 'chem-004',
        chineseName: '乙醇',
        englishName: 'Ethanol',
        casNumber: '64-17-5',
        dangerLevel: 1,
        departmentId: 'dept-003',
        departmentName: '品質管理部',
        hazardClassifications: ['易燃液體 類別2', '眼睛刺激 類別2'],
        lastUpdated: '2024-10-05',
        nextReviewDate: '2026-10-05',
        riskScore: 12.0,
        riskLevel: 'I'
      },
      {
        id: 'chem-005',
        chineseName: '氫氧化鈉',
        englishName: 'Sodium Hydroxide',
        casNumber: '1310-73-2',
        dangerLevel: 2,
        departmentId: 'dept-002',
        departmentName: '生產製造部',
        hazardClassifications: ['金屬腐蝕物 類別1', '皮膚腐蝕/刺激 類別1A'],
        lastUpdated: '2024-11-18',
        nextReviewDate: '2025-05-18',
        riskScore: 45.3,
        riskLevel: 'III'
      },
      {
        id: 'chem-006',
        chineseName: '甲苯',
        englishName: 'Toluene',
        casNumber: '108-88-3',
        dangerLevel: 2,
        departmentId: 'dept-004',
        departmentName: '倉儲運輸部',
        hazardClassifications: ['易燃液體 類別2', '生殖毒性 類別2', '特定標的器官毒性 類別3'],
        lastUpdated: '2024-03-10',
        nextReviewDate: '2025-03-10',
        riskScore: 64.2,
        riskLevel: 'IV'
      },
      {
        id: 'chem-007',
        chineseName: '硫酸',
        englishName: 'Sulfuric acid',
        casNumber: '7664-93-9',
        dangerLevel: 3,
        departmentId: 'dept-003',
        departmentName: '品質管理部',
        hazardClassifications: ['皮膚腐蝕/刺激 類別1A', '金屬腐蝕物 類別1'],
        lastUpdated: '2023-08-12',
        nextReviewDate: '2024-08-12',
        riskScore: 72.8,
        riskLevel: 'IV'
      }
    ];
  }

  loadDepartmentData(): void {
    // Calculate department stats from chemicals
    const deptMap = new Map<string, DepartmentData>();

    deptMap.set('dept-001', {
      id: 'dept-001',
      name: '實驗研發部',
      location: '3樓實驗室',
      chemicalCount: 0,
      highRiskCount: 0,
      riskScore: 0
    });

    deptMap.set('dept-002', {
      id: 'dept-002',
      name: '生產製造部',
      location: '1樓生產線',
      chemicalCount: 0,
      highRiskCount: 0,
      riskScore: 0
    });

    deptMap.set('dept-003', {
      id: 'dept-003',
      name: '品質管理部',
      location: '2樓QC實驗室',
      chemicalCount: 0,
      highRiskCount: 0,
      riskScore: 0
    });

    deptMap.set('dept-004', {
      id: 'dept-004',
      name: '倉儲運輸部',
      location: '地下一樓倉庫',
      chemicalCount: 0,
      highRiskCount: 0,
      riskScore: 0
    });

    // Calculate stats
    this.chemicals.forEach(chemical => {
      if (deptMap.has(chemical.departmentId)) {
        const dept = deptMap.get(chemical.departmentId)!;
        dept.chemicalCount++;
        dept.riskScore += chemical.riskScore || 0;

        if ((chemical.riskLevel === 'IV' || chemical.riskLevel === 'V') && chemical.riskScore) {
          dept.highRiskCount++;
        }
      }
    });

    // Calculate average risk score
    deptMap.forEach(dept => {
      if (dept.chemicalCount > 0) {
        dept.riskScore = Math.round((dept.riskScore / dept.chemicalCount) * 10) / 10;
      }
    });

    this.departments = Array.from(deptMap.values());
  }

  loadTodoItems(): void {
    // Mock todo items based on chemicals
    this.todoItems = [
      {
        id: 'todo-001',
        title: '更新硫酸SDS',
        description: '硫酸SDS即將過期，需要更新',
        dueDate: '2024-08-12',
        priority: 'high',
        status: 'pending',
        type: 'sds-update'
      },
      {
        id: 'todo-002',
        title: '完成第二季度公共危險品申報',
        description: '依法規要求完成本季公共危險品申報作業',
        dueDate: '2025-06-30',
        priority: 'high',
        status: 'pending',
        type: 'legal-report'
      },
      {
        id: 'todo-003',
        title: '評估苯的替代品',
        description: '尋找並評估低風險替代品以替代高風險苯的使用',
        dueDate: '2025-07-15',
        priority: 'high',
        status: 'pending',
        type: 'substitution'
      },
      {
        id: 'todo-004',
        title: '甲醛風險控制措施實施',
        description: '在生產線安裝局部排氣裝置以降低甲醛暴露風險',
        dueDate: '2025-06-10',
        priority: 'high',
        status: 'pending',
        type: 'control-measure'
      },
      {
        id: 'todo-005',
        title: '完成甲苯SDS審核',
        description: '完成甲苯SDS的定期審核作業',
        dueDate: '2025-03-10',
        priority: 'medium',
        status: 'pending',
        type: 'sds-review'
      },
      {
        id: 'todo-006',
        title: '更新氫氧化鈉安全資料表',
        description: '根據最新法規要求更新氫氧化鈉的安全資料表',
        dueDate: '2025-05-18',
        priority: 'medium',
        status: 'pending',
        type: 'sds-update'
      }
    ];
  }

  initializeCharts(): void {
    this.initHighRiskChemicalsChart();
    this.initDepartmentRiskChart();
    this.initChemicalCountChart();
    this.initExpiryStatusChart();
  }

  initHighRiskChemicalsChart(): void {
    // Get high risk chemicals (level IV and V)
    const highRiskChemicals = this.chemicals.filter(
      chemical => chemical.riskLevel === 'IV' || chemical.riskLevel === 'V'
    ).sort((a, b) => (b.riskScore || 0) - (a.riskScore || 0));

    const chemNames = highRiskChemicals.map(chem => chem.chineseName);
    const riskScores = highRiskChemicals.map(chem => chem.riskScore);

    this.highRiskChartOption = {
      title: {
        text: '高風險化學品',
        left: 'center',
        textStyle: {
          color: '#484949'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        name: '風險分數'
      },
      yAxis: {
        type: 'category',
        data: chemNames,
        axisLabel: {
          interval: 0
        }
      },
      series: [
        {
          name: '風險分數',
          type: 'bar',
          data: riskScores,
          itemStyle: {
            color: function(params: any) {
              const value = params.value;
              if (value >= 80) return '#e74c3c'; // Level V
              if (value >= 60) return '#f5820d'; // Level IV
              return '#f5820d'; // Other (shouldn't appear in this chart)
            }
          },
          label: {
            show: true,
            position: 'right',
            formatter: '{c}'
          }
        }
      ]
    };
  }

  initDepartmentRiskChart(): void {
    const departmentNames = this.departments.map(dept => dept.name);
    const riskScores = this.departments.map(dept => dept.riskScore);
    const highRiskCounts = this.departments.map(dept => dept.highRiskCount);

    this.departmentRiskChartOption = {
      title: {
        text: '部門風險分析',
        left: 'center',
        textStyle: {
          color: '#484949'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      legend: {
        data: ['平均風險分數', '高風險化學品數量'],
        top: '10%'
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
        top: '20%'
      },
      xAxis: {
        type: 'category',
        data: departmentNames
      },
      yAxis: [
        {
          type: 'value',
          name: '風險分數',
          min: 0,
          max: 100
        },
        {
          type: 'value',
          name: '數量',
          min: 0,
          max: Math.max(...highRiskCounts) + 1
        }
      ],
      series: [
        {
          name: '平均風險分數',
          type: 'bar',
          data: riskScores,
          itemStyle: {
            color: '#f5820d'
          }
        },
        {
          name: '高風險化學品數量',
          type: 'line',
          yAxisIndex: 1,
          data: highRiskCounts,
          itemStyle: {
            color: '#e74c3c'
          }
        }
      ]
    };
  }

  initChemicalCountChart(): void {
    const departmentNames = this.departments.map(dept => dept.name);
    const chemicalCounts = this.departments.map(dept => dept.chemicalCount);

    this.chemicalCountChartOption = {
      title: {
        text: '各部門化學品數量',
        left: 'center',
        textStyle: {
          color: '#484949'
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)'
      },
      legend: {
        orient: 'vertical',
        left: 10,
        top: 'center',
        data: departmentNames
      },
      series: [
        {
          name: '化學品數量',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: true,
            formatter: '{b}: {c}件'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 16,
              fontWeight: 'bold'
            }
          },
          labelLine: {
            show: true
          },
          data: departmentNames.map((name, index) => {
            return {
              name: name,
              value: chemicalCounts[index]
            };
          }),
          color: ['#f5820d', '#2e9116', '#ffecc5', '#484949']
        }
      ]
    };
  }

  initExpiryStatusChart(): void {
    const currentDate = new Date();
    const expired = this.chemicals.filter(chem => new Date(chem.nextReviewDate) < currentDate).length;
    const expiringSoon = this.chemicals.filter(chem => {
      const reviewDate = new Date(chem.nextReviewDate);
      const threeMonthsFromNow = new Date();
      threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
      return reviewDate >= currentDate && reviewDate <= threeMonthsFromNow;
    }).length;
    const valid = this.chemicals.length - expired - expiringSoon;

    this.expiryStatusChartOption = {
      title: {
        text: 'SDS審核狀態',
        left: 'center',
        textStyle: {
          color: '#484949'
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)'
      },
      legend: {
        orient: 'horizontal',
        bottom: '0%',
        data: ['有效', '即將到期', '已過期']
      },
      series: [
        {
          name: 'SDS狀態',
          type: 'pie',
          radius: '70%',
          center: ['50%', '50%'],
          data: [
            { value: valid, name: '有效', itemStyle: { color: '#2e9116' } },
            { value: expiringSoon, name: '即將到期', itemStyle: { color: '#f5820d' } },
            { value: expired, name: '已過期', itemStyle: { color: '#e74c3c' } }
          ],
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }
      ]
    };
  }

  setActiveTab(tab: 'overview' | 'chemicals' | 'departments' | 'todos'): void {
    this.activeTab = tab;
  }

  // Utility methods for data display
  getHighRiskChemicals(): ChemicalData[] {
    return this.chemicals
      .filter(chem => chem.riskLevel === 'IV' || chem.riskLevel === 'V')
      .sort((a, b) => (b.riskScore || 0) - (a.riskScore || 0));
  }

  getExpiredSDS(): ChemicalData[] {
    const currentDate = new Date();
    return this.chemicals
      .filter(chem => new Date(chem.nextReviewDate) < currentDate)
      .sort((a, b) => new Date(a.nextReviewDate).getTime() - new Date(b.nextReviewDate).getTime());
  }

  getExpiringSoonSDS(): ChemicalData[] {
    const currentDate = new Date();
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(currentDate.getMonth() + 3);

    return this.chemicals
      .filter(chem => {
        const reviewDate = new Date(chem.nextReviewDate);
        return reviewDate >= currentDate && reviewDate <= threeMonthsFromNow;
      })
      .sort((a, b) => new Date(a.nextReviewDate).getTime() - new Date(b.nextReviewDate).getTime());
  }

  getUrgentTodos(): TodoItem[] {
    const currentDate = new Date();
    const oneMonthFromNow = new Date();
    oneMonthFromNow.setMonth(currentDate.getMonth() + 1);

    return this.todoItems
      .filter(todo => {
        const dueDate = new Date(todo.dueDate);
        return todo.status === 'pending' && dueDate <= oneMonthFromNow;
      })
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
  }

  getHighRiskDepartments(): DepartmentData[] {
    return this.departments
      .filter(dept => dept.highRiskCount > 0 || dept.riskScore >= 60)
      .sort((a, b) => b.riskScore - a.riskScore);
  }

  // Date formatting
  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  }

  getHighRiskChemicalsForDept(departmentId: string, limit: number = 0): ChemicalData[] {
    if (!this.chemicals) return [];

    const filteredChemicals = this.chemicals.filter(c =>
      c.departmentId === departmentId &&
      (c.riskLevel === 'IV' || c.riskLevel === 'V')
    );

    return limit > 0 ? filteredChemicals.slice(0, limit) : filteredChemicals;
  }

  /**
   * Gets the count of high risk chemicals for a department
   */
  getHighRiskChemicalCountForDept(departmentId: string): number {
    if (!this.chemicals) return 0;

    return this.chemicals.filter(c =>
      c.departmentId === departmentId &&
      (c.riskLevel === 'IV' || c.riskLevel === 'V')
    ).length;
  }

}
